import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  Notice,
  Stat,
} from '../../../components/ui';
import { Icon } from '../../../components/Icon';
import { useToast } from '../../../components/Toast';
import { api } from '../../../lib/api';
import { money, num } from '../../../lib/format';
import { useAuth } from '../../../auth/AuthProvider';

interface CostCenterRow {
  id: string;
  code: string;
  name: string;
  notes: string | null;
  isActive: boolean;
  unitCount: number;
  employeeCount: number;
  monthlyPayroll: number;
}

/**
 * مراكز التكلفة.
 *
 * الوحدةُ التنظيميّة تقول من يتبع من، ومركزُ التكلفة يقول على من تُحمَّل
 * النفقة — وهما لا يتطابقان دائمًا: فريقٌ واحدٌ قد يعمل على مشروعين
 * بميزانيتين، ومشروعٌ واحدٌ قد يجمع أناسًا من ثلاثة أقسام. فلو حُسبت
 * الكلفةُ بالهيكل وحده لما أمكن أن يُسأل «كم كلّفنا هذا الخطّ؟».
 *
 * والترتيبُ بالأثقل رواتبَ أوّلًا لا بالكود: من يفتح هذه الشاشة يسأل
 * «أين تذهب الرواتب؟»، وترتيبٌ أبجديٌّ يجعله يبحث عن جوابٍ كان يمكن أن
 * يقرأه في السطر الأوّل.
 */
export function CostCentersSection() {
  const { atLeast } = useAuth();
  const canEdit = atLeast('ADMIN');
  const [editing, setEditing] = useState<CostCenterRow | 'new' | null>(null);

  const list = useQuery({
    queryKey: ['hr', 'setup', 'cost-centers'],
    queryFn: () => api<CostCenterRow[]>('/hr/setup/cost-centers'),
  });

  const rows = list.data ?? [];

  /* الأثقلُ أوّلًا، وعند التساوي الأكثرُ موظّفين — لأنّ صفرًا على مركزَين
     مختلفَين ليس خبرًا واحدًا. */
  const sorted = rows
    .slice()
    .sort(
      (a, b) =>
        b.monthlyPayroll - a.monthlyPayroll ||
        b.employeeCount - a.employeeCount ||
        a.code.localeCompare(b.code),
    );

  const activeCount = rows.filter((c) => c.isActive).length;
  const totalPayroll = rows.reduce((s, c) => s + c.monthlyPayroll, 0);
  const emptyCount = rows.filter((c) => c.employeeCount === 0).length;

  /* مركزٌ موقوفٌ ما زال يحمل رواتب: حالةٌ تُرى أو تُفاجئ عند أوّل تقرير. */
  const stoppedPayroll = rows
    .filter((c) => !c.isActive)
    .reduce((s, c) => s + c.monthlyPayroll, 0);

  return (
    <>
      <div className="card">
        <div className="card-head">
          <h2 className="card-title">مراكز التكلفة</h2>
          <span className="tiny mute">
            عليها تُحمَّل رواتبُ من يتبعها، فتُقرأ كلفةُ كلِّ وحدةٍ على حدة
          </span>
          <span style={{ marginInlineStart: 'auto' }}>
            {canEdit && (
              <button className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>
                <Icon name="plus" size={15} />
                مركز جديد
              </button>
            )}
          </span>
        </div>
      </div>

      {list.isError && (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      )}

      {list.isPending && <div className="skeleton" style={{ height: 200 }} />}

      {rows.length > 0 && (
        <div className="stats">
          <Stat
            label="مراكز مفعّلة"
            value={num(activeCount)}
            note={
              activeCount < rows.length
                ? `و${num(rows.length - activeCount)} موقوفة`
                : undefined
            }
          />
          <Stat
            label="إجمالي الرواتب الشهرية عليها"
            value={money(totalPayroll)}
            note={
              stoppedPayroll > 0
                ? `منها ${money(stoppedPayroll)} على مراكز موقوفة`
                : undefined
            }
          />
          {/*
            المطلوبُ أصلًا كان «كم راتبًا بلا مركز» — وهو ما لا تعرفه هذه
            الشاشة: المسارُ يعيد المراكزَ وما عليها، ولا يعيد من لا مركزَ
            له. فيُعرض ما يمكن قولُه بصدق، ويُقال الناقصُ في سطرٍ تحته.
          */}
          <Stat
            label="مراكز بلا موظّفين"
            value={num(emptyCount)}
            note="مركزٌ فارغٌ إمّا نسيه أحدٌ عند ربط الموظّفين، وإمّا انتهى عملُه فيُوقَف"
          />
        </div>
      )}

      {/*
        الرقمُ هنا أساسيٌّ لا مصروف: مجموعُ الرواتب الأساسيّة لمن هم على
        كشف الرواتب اليوم. أمّا مسيّرُ الشهر فيضيف بدلاتٍ وحوافزَ ويخصم
        تأميناتٍ وسلفًا — فلا يتطابق الرقمان أبدًا، ومن يقارنهما دون أن
        يعرف الفرقَ يظنّ أنّ أحدهما خطأ.
      */}
      <Notice title="الرقم أساسيّ لا مسيَّر" icon="wallet">
        «الرواتب الشهرية» هنا مجموعُ <strong>الرواتب الأساسية</strong> لمن هم
        على كشف الرواتب الآن — ليست إجماليَّ مسيّر الرواتب، فالمسيَّر يحمل
        فوقها بدلاتٍ وحوافزَ ويخصم منها تأميناتٍ وضرائبَ وسلفًا. كذلك لا
        تُظهر هذه الشاشة الرواتبَ التي لا مركزَ لها أصلًا: من لم يُربط
        بمركزٍ لا يظهر في أيّ صفٍّ هنا، ويُعرف عددُه من ملفّات الموظّفين.
      </Notice>

      {list.data && rows.length === 0 && (
        <EmptyState
          title="لا مراكز تكلفة بعد"
          text="بلا مركزٍ تُجمع كلُّ الرواتب في رقمٍ واحدٍ للشركة، فلا يُعرف أيُّ خطٍّ يكسب وأيُّه يأكل — والوحداتُ وحدَها لا تكفي، لأنّ المشروع الواحد قد يجمع أناسًا من ثلاثة أقسام."
          action={
            canEdit ? (
              <button className="btn btn-primary" onClick={() => setEditing('new')}>
                أضف أوّل مركز
              </button>
            ) : undefined
          }
        />
      )}

      {sorted.length > 0 && (
        <div className="card">
          <div className="table-wrap sticky-head">
            <table className="table">
              <thead>
                <tr>
                  <th>الكود</th>
                  <th>المركز</th>
                  <th>الوحدات</th>
                  <th>الموظّفون</th>
                  <th>الرواتب الشهرية</th>
                  <th>الحالة</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {sorted.map((c) => (
                  <tr key={c.id}>
                    <td className="num cell-mute" dir="ltr">
                      {c.code}
                    </td>
                    <td>
                      <span className="cell-strong">{c.name}</span>
                      {c.notes && <div className="tiny mute">{c.notes}</div>}
                    </td>
                    <td className="num">
                      {c.unitCount > 0 ? num(c.unitCount) : '—'}
                    </td>
                    <td className="num">
                      {c.employeeCount > 0 ? num(c.employeeCount) : '—'}
                    </td>
                    <td className="num">
                      {c.monthlyPayroll > 0 ? money(c.monthlyPayroll) : '—'}
                    </td>
                    <td>
                      <Badge kind={c.isActive ? 'success' : 'neutral'}>
                        {c.isActive ? 'مفعَّل' : 'موقوف'}
                      </Badge>
                    </td>
                    <td>
                      {canEdit && (
                        <button className="btn btn-sm" onClick={() => setEditing(c)}>
                          تعديل
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {sorted.length > 0 && (
        <p className="tiny mute mt-2">
          «الموظّفون» هم من على كشف الرواتب اليوم. فمركزٌ خدمه ناسٌ ثمّ تركوا
          الشركة يُقرأ هنا صفرًا، وتبقى حركتُه القديمة في التقارير.
        </p>
      )}

      {editing && (
        <CostCenterDialog
          center={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function CostCenterDialog({
  center,
  onClose,
}: {
  center: CostCenterRow | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [code, setCode] = useState(center?.code ?? '');
  const [name, setName] = useState(center?.name ?? '');
  const [notes, setNotes] = useState(center?.notes ?? '');
  const [isActive, setIsActive] = useState(center?.isActive ?? true);

  const valid = code.trim().length >= 1 && name.trim().length >= 2;

  const save = useMutation({
    mutationFn: () => {
      const body = {
        code: code.trim(),
        name: name.trim(),
        notes: notes.trim() || null,
        isActive,
      };
      return center
        ? api(`/hr/setup/cost-centers/${center.id}`, { method: 'PATCH', body })
        : api('/hr/setup/cost-centers', { method: 'POST', body });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['hr'] });
      toast(center ? 'حُفظ المركز' : 'أُضيف المركز');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  return (
    <Modal
      title={center ? `تعديل ${center.name}` : 'مركز تكلفة جديد'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field">
          <label className="label req" htmlFor="cc-code">
            الكود
          </label>
          <input
            id="cc-code"
            className="input num"
            dir="ltr"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="CC-01"
            maxLength={20}
          />
          {/* الكودُ هو ما يُكتب في القيد المحاسبيّ ويُقرأ في التصدير. */}
          <span className="tiny mute">
            به يُعرف المركزُ في القيود والتصدير — أقصرُ من الاسم وأثبتُ منه.
          </span>
        </div>

        <div className="field">
          <label className="label req" htmlFor="cc-name">
            اسم المركز
          </label>
          <input
            id="cc-name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="خط الإنتاج الأول"
            maxLength={120}
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="cc-notes">
            ملاحظات
          </label>
          <textarea
            id="cc-notes"
            className="textarea"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="ما الذي يُحمَّل على هذا المركز وما الذي لا يُحمَّل عليه"
            maxLength={1000}
          />
          <span className="tiny mute">
            حدُّ ما يدخل المركزَ يُكتب مرّةً هنا، فلا يُجتهد فيه كلَّ شهر.
          </span>
        </div>

        <div className="field span-2">
          <label className="row tiny" style={{ gap: 6 }}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            مفعَّل
          </label>
          {/* لا حذفَ: وحداتٌ وموظّفون وقيودٌ قديمة تشير إليه. */}
          <span className="tiny mute">
            الإيقافُ لا الحذف — المركزُ الموقوف لا يُختار في الملفّات الجديدة،
            ويبقى على ما سبق ربطُه به، فتبقى تقاريرُ الشهور الماضية كما كانت.
          </span>
        </div>
      </div>
    </Modal>
  );
}
